import type { ActDirector } from "../acts/ActDirector";

export type EnemyKind = "drone" | "glyph" | "warden";

export type EnemySpawn = {
  kind: EnemyKind;
  x: number;
  y: number;
  vx: number;
  vy: number;
  hp: number;
  /** Seconds between shots for this unit */
  fireInterval: number;
  /** Initial delay before first shot */
  fireDelay: number;
};

/**
 * SPAWN LOGIC ONLY — creates enemies.
 *
 * Does NOT spawn free-floating bullets.
 * Bullets are fired by living enemies in GameApp from their (x,y).
 */
export class Spawner {
  private enemyCd = 3.5;
  private width: number;
  private height: number;
  private age = 0;
  private readonly safeTime = 3.5;

  constructor(width: number, height: number) {
    this.width = width;
    this.height = height;
  }

  setSize(w: number, h: number): void {
    this.width = w;
    this.height = h;
  }

  reset(): void {
    this.enemyCd = 3.5;
    this.age = 0;
  }

  /**
   * @returns new enemies to add this frame (empty during open grace)
   */
  update(dt: number, act: ActDirector): EnemySpawn[] {
    this.age += dt;
    this.enemyCd -= dt;
    if (this.age < this.safeTime) return [];
    if (this.enemyCd > 0) return [];

    const dens = act.density;
    const id = act.current.id;
    const early = this.age < 12 ? 1.55 : this.age < 30 ? 1.25 : 1;
    this.enemyCd = (1.25 / dens) * early * (0.7 + Math.random() * 0.55);

    const out: EnemySpawn[] = [];
    const lane = 0.15 + Math.random() * 0.7;
    const x = this.width * lane;
    const kind: EnemyKind =
      id >= 4
        ? "warden"
        : id >= 2
          ? Math.random() > 0.45
            ? "glyph"
            : "drone"
          : "drone";

    out.push(this.makeEnemy(kind, x, -24, act));

    // Dual lane mid/late acts
    if (id >= 3 && Math.random() < 0.4) {
      out.push(
        this.makeEnemy(
          "drone",
          this.width * (1 - lane),
          -36,
          act,
        ),
      );
    }

    return out;
  }

  private makeEnemy(
    kind: EnemyKind,
    x: number,
    y: number,
    act: ActDirector,
  ): EnemySpawn {
    const hp = kind === "warden" ? 4 : kind === "glyph" ? 2 : 1;
    const vy = kind === "warden" ? 48 : 72 + act.intensity * 36;
    const vx = (Math.random() - 0.5) * 36;
    // Fire cadence by type (actual shots happen on the living unit)
    const fireInterval =
      kind === "warden" ? 0.7 : kind === "glyph" ? 0.95 : 1.15;
    return {
      kind,
      x,
      y,
      vx,
      vy,
      hp,
      fireInterval,
      fireDelay: 0.4 + Math.random() * 0.5,
    };
  }
}
